import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import WalletConnectProvider from '@walletconnect/web3-provider';
import { POLYGON_CHAIN_ID_MAINNET, CONTRACTS } from '../config';
import { RPC_MAINNET } from '../config';
import DolphinABI from '../abis/Dolphin.json';

const WalletContext = createContext();

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(new ethers.providers.JsonRpcProvider(RPC_MAINNET));
  const [signer, setSigner] = useState(null);
  const [dolphinContract, setDolphinContract] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Set your super admin address here (can be a list for multiple admins)
  const SUPER_ADMIN = '0x789635562722d63736C2f8640924882CE8Df4E91'.toLowerCase();
  const [chainId, setChainId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0); // force context update

  // Detect any EVM wallet provider (TokenPocket, MetaMask, Trust Wallet, etc.)
  const getEvmProvider = () => {
    // When multiple wallets are installed, window.ethereum.providers is an array
    if (window.ethereum && Array.isArray(window.ethereum.providers)) {
      const tpProvider = window.ethereum.providers.find(p => p.isTokenPocket);
      if (tpProvider) return tpProvider;
      const twProvider = window.ethereum.providers.find(p => p.isTrust);
      if (twProvider) return twProvider;
      return window.ethereum.providers[0];
    }
    // TokenPocket desktop: injected directly at window.ethereum
    if (window.ethereum && window.ethereum.isTokenPocket) {
      return window.ethereum;
    }
    // TokenPocket mobile wallet detection
    if (window.tp && window.tp.isTokenPocket) {
      return window.tp;
    }
    // Trust Wallet desktop
    if (window.ethereum && window.ethereum.isTrust) {
      return window.ethereum;
    }
    // Fallback to window.ethereum (MetaMask, Brave, Coinbase, etc.)
    if (window.ethereum) {
      return window.ethereum;
    }
    return null;
  };

  const POLYGON_CHAIN_ID_HEX = '0x89';

  const switchToPolygon = async (evmProvider) => {
    const provider = evmProvider || window.ethereum;
    if (!provider) return;
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: POLYGON_CHAIN_ID_HEX }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: POLYGON_CHAIN_ID_HEX,
            chainName: 'Polygon Mainnet',
            nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
            rpcUrls: [RPC_MAINNET], // Always use premium Alchemy RPC
            blockExplorerUrls: ['https://polygonscan.com/'],
          }],
        });
      }
    }
  };

  const initContract = useCallback(async (signerOrProvider) => {
    const contract = new ethers.Contract(CONTRACTS.DOLPHIN, DolphinABI, signerOrProvider);
    setDolphinContract(contract);
    console.log('[DOLPHIN] Contract initialized:', {
      address: CONTRACTS.DOLPHIN,
      abi: DolphinABI,
      providerOrSigner: signerOrProvider
    });
    return contract;
  }, []);




  // Accept walletType: 'metamask' | 'tokenpocket' | 'walletconnect' | undefined
  const connectWallet = async (walletType) => {
    let evmProvider = null;
    let isWalletConnect = false;
    // Improved MetaMask detection for mobile and web
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (walletType === 'tokenpocket') {
      // Only use a provider that is TokenPocket
      if (window.ethereum && Array.isArray(window.ethereum.providers)) {
        const tpProvider = window.ethereum.providers.find(p => p.isTokenPocket);
        if (tpProvider) evmProvider = tpProvider;
      }
      if (!evmProvider && window.tp && window.tp.isTokenPocket) evmProvider = window.tp;
      if (!evmProvider && window.ethereum && window.ethereum.isTokenPocket) evmProvider = window.ethereum;
      // Only deep link if no provider is detected and user is on mobile
      if (!evmProvider) {
        if (isMobile) {
          const dappUrl = encodeURIComponent(window.location.href);
          window.open(`https://tp-lab.tokenpocket.pro/index.html?action=dapp&url=${dappUrl}`, '_blank');
          setLoading(false);
          return;
        } else {
          // On web, redirect to TokenPocket install page
          window.open('https://tokenpocket.pro/download', '_blank');
          setLoading(false);
          return;
        }
      }
    } else if (walletType === 'trustwallet') {
      // Only use a provider that is Trust Wallet
      if (window.ethereum && Array.isArray(window.ethereum.providers)) {
        const twProvider = window.ethereum.providers.find(p => p.isTrust);
        if (twProvider) evmProvider = twProvider;
      }
      if (!evmProvider && window.ethereum && window.ethereum.isTrust) evmProvider = window.ethereum;
      if (!evmProvider && isMobile) {
        window.open('https://link.trustwallet.com/open_url?coin_id=60&url=' + encodeURIComponent(window.location.href), '_blank');
        setLoading(false);
        return;
      } else if (!evmProvider) {
        // On web, redirect to Trust Wallet install page
        window.open('https://trustwallet.com/download', '_blank');
        setLoading(false);
        return;
      }
    } else if (walletType === 'walletconnect') {
      // Only use WalletConnect
      // (no fallback, always use WalletConnectProvider)
      // WalletConnect provider setup
      evmProvider = new WalletConnectProvider({
        rpc: {
          137: RPC_MAINNET,
        },
        chainId: 137,
      });
      isWalletConnect = true;
      try {
        await evmProvider.enable();
      } catch (err) {
        setError('WalletConnect connection cancelled.');
        setLoading(false);
        return;
      }
    } else {
      evmProvider = getEvmProvider();
    }
    if (!evmProvider) {
      setError('No wallet detected. Please install TokenPocket or Trust Wallet.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let accounts = [];
      let currentChainId = null;
      if (isWalletConnect) {
        const web3Provider = new ethers.providers.Web3Provider(evmProvider);
        accounts = await web3Provider.listAccounts();
        currentChainId = (await web3Provider.getNetwork()).chainId;
      } else {
        accounts = await evmProvider.request({ method: 'eth_requestAccounts' });
        const rawChainId = await evmProvider.request({ method: 'eth_chainId' });
        currentChainId = parseInt(rawChainId, 16);
      }
      console.log('connectWallet debug:', { accounts, currentChainId });


      // For WalletConnect, chain switching is not supported in v1, so just warn
      if (currentChainId !== 137 && !isWalletConnect) {
        setError('Please switch your wallet to Polygon Mainnet (chainId 137).');
        console.error('Wrong chainId:', currentChainId);
        try {
          await switchToPolygon(evmProvider);
          const rawChainId = await evmProvider.request({ method: 'eth_chainId' });
          currentChainId = parseInt(rawChainId, 16);
        } catch (err) {
          setError('Failed to switch to Polygon Mainnet. Please switch manually in your wallet.');
          setLoading(false);
          return;
        }
        if (currentChainId !== POLYGON_CHAIN_ID_MAINNET) {
          setError('Failed to switch to Polygon Mainnet. Please switch manually in your wallet.');
          setLoading(false);
          return;
        }
      } else if (isWalletConnect && currentChainId !== 137) {
        setError('Please connect to Polygon Mainnet in your wallet app.');
        setLoading(false);
        return;
      }

      const web3Provider = new ethers.providers.Web3Provider(evmProvider);
      const web3Signer = web3Provider.getSigner();
      // Always use premium RPC for reads, but use wallet provider for signer
      setProvider(new ethers.providers.JsonRpcProvider(RPC_MAINNET));
      setSigner(web3Signer);
      setAccount(accounts[0]);
      setChainId(currentChainId);

      const contract = await initContract(web3Signer);
      setVersion(v => v + 1); // force context consumers to update
      try {
        const ownerAddress = await contract.owner();
        const userAddress = accounts[0].toLowerCase();
        setIsOwner(ownerAddress.toLowerCase() === userAddress);
        setIsAdmin(ownerAddress.toLowerCase() === userAddress || userAddress === SUPER_ADMIN);
      } catch (err) {
        setIsOwner(false);
        setIsAdmin(false);
      }
      localStorage.setItem('walletConnected', 'true');
    } catch (err) {
      // Handle invalid chain id error from ethers
      console.error('connectWallet error:', err);
      if (err.code === 'INVALID_ARGUMENT' && err.message && err.message.includes('invalid chain id')) {
        setError('Invalid chain id for signer. Please switch your wallet to Polygon Mainnet (chainId 137).');
      } else {
        setError(typeof err.message === 'string' ? err.message : 'Wallet connection failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setDolphinContract(null);
    setIsOwner(false);
    setIsAdmin(false);
    setChainId(null);
    setVersion(v => v + 1); // force context consumers to update
    localStorage.removeItem('walletConnected');
  };

  useEffect(() => {
    const evmProvider = getEvmProvider();
    if (evmProvider) {
      evmProvider.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          setAccount(accounts[0]);
        }
      });

      evmProvider.on('chainChanged', (newChainId) => {
        setChainId(newChainId);
        window.location.reload();
      });
    }

    // Remove auto-connect on landing for explicit wallet choice



    return () => {
      const evmProvider = getEvmProvider();
      if (evmProvider) {
        evmProvider.removeAllListeners('accountsChanged');
        evmProvider.removeAllListeners('chainChanged');
      }
    };
  }, [dolphinContract, account]);

  const value = {
    account,
    provider,
    signer,
    dolphinContract,
    isOwner,
    isAdmin,
    chainId,
    loading,
    error,
    connectWallet,
    disconnectWallet,
    isConnected: !!account,
    isPolygon: chainId === POLYGON_CHAIN_ID_MAINNET,
    version,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
